import { TableCell } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { orange } from '@material-ui/core/colors';
import { withStyles } from '@material-ui/core/styles';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PostAddIcon from '@material-ui/icons/PostAdd';
import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import styled from 'styled-components';
import Box from '../../../components/Box';
import BoxBody from '../../../components/Box/BoxBody';
import BoxHeader from '../../../components/Box/BoxHeader';
import DataDisplayField from '../../../components/DataDisplay/DataDisplayField';
import DataTable from '../../../components/DataTable_v2/DataTable';
import Layout from '../../../components/Layout';
import { LOADING } from '../../../enums/states';
import useReduxReducer from '../../../hooks/useReduxReducer';
import { getClosureItemsAction, getClosureStatsAction, getClosureTransactionsAction } from './actions';
import ItemsList from './components/ItemsList';
import TransactionsList from './components/TransactionsList';
import closureData from './reducers';
import DescriptionIcon from '@material-ui/icons/Description';

const MainContentWrapper = styled.div`
  padding: 20px;
`;

const ColorButton = withStyles(() => ({
  root: {
    textTransform: 'lowercase',
    color: 'white',
    backgroundColor: orange[500],
    '&:hover': {
      backgroundColor: orange[700],
    },
  },
}))(Button);

const ClosureDetailPage = () => {
  useReduxReducer({ closureData });
  const [vatStats, setVatStats] = useState([]);

  let { id: closureId } = useParams();
  const dispatch = useDispatch();

  const statistics = useSelector((state) => state?.closureData?.statistics?.data);
  const transactions = useSelector((state) => state?.closureData?.transactions?.data);
  const transactionsMeta = useSelector((state) => state?.closureData?.transactions?.metadata);

  const items = useSelector((state) => state?.closureData?.items?.data);
  const itemsMeta = useSelector((state) => state?.closureData?.items?.metadata);

  useEffect(() => {
    if (statistics?.vatStatisticsByGroup) {
      const data = statistics?.vatStatisticsByGroup?.reduce((output, group, index) => {
        output = [
          ...output,
          { type: 'header', name: group.name },
          ...group.lines.map((line, index) => ({
            type: 'line',
            index,
            ...line,
          })),
        ];

        return output;
      }, []);

      const vatSummary = [
        { type: 'header', name: 'celkem' },
        ...statistics?.vatStatistics.map((line, index) => ({
          type: 'line',
          index,
          ...line,
        })),
      ];

      setVatStats([...data, ...vatSummary]);
    }
  }, [statistics]);

  const refreshTransactionsData = (page, pageSize) => {
    dispatch(getClosureTransactionsAction(closureId, page, pageSize));
  };

  const refreshItemsData = (page, pageSize) => {
    dispatch(getClosureItemsAction(closureId, page, pageSize));
  };

  useEffect(() => {
    dispatch(getClosureStatsAction(closureId));
  }, [closureId, dispatch]);

  const closure = statistics?.closure;

  const history = useHistory();

  return (
    <Layout>
      <MainContentWrapper>
        <GridRow>
          <ContentCard
            title={'Uzávěrka pokladny ' + closure?.documentNo}
            icon={<AssignmentIcon />}
            color={'#43a047'}
            actions={[
              <ColorButton
                key={'print'}
                variant="contained"
                color="secondary"
                endIcon={<PostAddIcon />}
                onClick={() => {
                  history.push(`${closureId}/print/`);
                }}>
                Tisk
              </ColorButton>,
            ]}>
            <GridRow>
              <DataColumn>
                <DataDisplayField label={'Čislo dokladu'} value={closure?.documentNo} />
                <DataDisplayField
                  label={'Datum a čas uzávěrky'}
                  value={
                    <>
                      <FormattedDate value={closure?.createdOn} /> <FormattedTime value={closure?.createdOn} />
                    </>
                  }
                />
                <DataDisplayField label={'Vytvořil'} value={closure?.name} />
              </DataColumn>
              <DataColumn>
                <DataDisplayField label={'Počet účtenek'} value={closure?.transactionCount} />
                <DataDisplayField
                  label={'Základ daně'}
                  value={<FormattedNumber value={closure?.grossAmount - closure?.taxAmount || 0} currency={'CZK'} {...{ style: 'currency' }} />}
                />
                <DataDisplayField label={'Daň'} value={<FormattedNumber value={closure?.taxAmount || 0} currency={'CZK'} {...{ style: 'currency' }} />} />
                <DataDisplayField label={'Celkem'} value={<FormattedNumber value={closure?.grossAmount || 0} currency={'CZK'} {...{ style: 'currency' }} />} />
              </DataColumn>
              <DataColumn></DataColumn>
              <DataColumn></DataColumn>
            </GridRow>
          </ContentCard>
        </GridRow>
        <GridRow>
          <ContentCard title={'Platby'} icon={<AccountBalanceWalletIcon />} color={'#f87e3c'}>
            <PaymentsTable data={statistics?.paymentStats || []} />
          </ContentCard>
          <ContentCard title={'DPH'} icon={<AccountBalanceIcon />} color={'#ce1b1b'}>
            <VatTable data={vatStats} />
          </ContentCard>
        </GridRow>
        <GridRow>
          <ContentCard title={'Účtenky'} icon={<DescriptionIcon />} color={'#88c8f5'}>
            <TransactionsList
              data={transactions?.transactions || []}
              count={transactions?.count || 0}
              loading={transactionsMeta?.fetchingData?.status === LOADING}
              refresh={refreshTransactionsData}
            />
          </ContentCard>
        </GridRow>
        <GridRow>
          <ContentCard title={'Položky'} icon={<DescriptionIcon />} color={'#4af869'}>
            <ItemsList data={items?.items || []} count={items?.count || 0} loading={itemsMeta?.fetchingData?.status === LOADING} refresh={refreshItemsData} />
          </ContentCard>
        </GridRow>
      </MainContentWrapper>
    </Layout>
  );
};

export default ClosureDetailPage;

const Row = styled.div`
  margin-bottom: 60px;
`;

const GridRow = styled.div`
  gap: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  justify-content: center;
  //align-items: center;
`;

const ContentCard = ({ title, children, icon, color, actions }) => {
  return (
    <Row>
      <Box>
        <BoxHeader icon={icon} color={color} title={title} actions={actions} />
        <BoxBody style={{ paddingBottom: '30px' }}>{children}</BoxBody>
      </Box>
    </Row>
  );
};

const PaymentsTable = ({ data }) => {
  return (
    <DataTable
      data={data}
      columns={[
        {
          id: 'paymentType',
          label: <FormattedMessage id={'closure.paymentType'} />,
          align: 'left',
        },
        {
          id: 'paymentAmount',
          label: <FormattedMessage id={'closure.paymentAmount'} />,
          align: 'right',
        },
        {
          id: 'paymentCount',
          label: <FormattedMessage id={'closure.paymentCount'} />,
          align: 'right',
        },
      ]}
      template={({ name, amount, count }) => (
        <>
          <TableCell>
            {name === 'CARD' && <FormattedMessage id={'payment.card'} />}
            {name === 'CASH' && <FormattedMessage id={'payment.cash'} />}
          </TableCell>
          <TableCell style={{ textAlign: 'right' }}>
            <FormattedNumber value={amount || 0} currency={'CZK'} {...{ style: 'currency' }} />
          </TableCell>
          <TableCell style={{ textAlign: 'right' }}>{count}</TableCell>
        </>
      )}
    />
  );
};

const VatTable = ({ data }) => {
  return (
    <DataTable
      data={data}
      columns={[
        {
          id: 'name',
          label: '',
          align: 'left',
        },
        {
          id: 'taxPercentage',
          label: <FormattedMessage id={'closure.taxPercentage'} />,
          align: 'right',
        },
        {
          id: 'netAmount',
          label: <FormattedMessage id={'closure.netAmount'} />,
          align: 'right',
        },
        {
          id: 'taxAmount',
          label: <FormattedMessage id={'closure.taxAmount'} />,
          align: 'right',
        },
        {
          id: 'grossAmount',
          label: <FormattedMessage id={'closure.grossAmount'} />,
          align: 'right',
        },
      ]}
      template={({ type, name, index, percentage, taxAmount, grossAmount }) => {
        return (
          <>
            {type === 'line' && (
              <>
                <TableCell style={{ textAlign: 'right' }} />
                <TableCell style={{ textAlign: 'right' }}>
                  <FormattedNumber value={percentage / 100 || 0} {...{ style: 'percent' }} />
                </TableCell>
                <TableCell style={{ textAlign: 'right' }}>
                  <FormattedNumber value={grossAmount - taxAmount || 0} currency={'CZK'} {...{ style: 'currency' }} />
                </TableCell>
                <TableCell style={{ textAlign: 'right' }}>
                  <FormattedNumber value={taxAmount || 0} currency={'CZK'} {...{ style: 'currency' }} />
                </TableCell>
                <TableCell style={{ textAlign: 'right' }}>
                  <FormattedNumber value={grossAmount || 0} currency={'CZK'} {...{ style: 'currency' }} />
                </TableCell>
              </>
            )}
            {type === 'header' && (
              <>
                <TableCell colSpan={5} style={{ backgroundColor: '#ccc' }}>
                  {name}
                </TableCell>
              </>
            )}
          </>
        );
      }}
    />
  );
};

const DataColumn = styled.div`
  padding: 20px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
